import { APP_URL } from 'utils/env';
import { isProjectHost } from 'utils/projectHost';

// Keep the console on exactly one origin.
//
// ---- Why ----
//
// The JWT lives in localStorage, which browsers scope per origin. When
// the same app answered on both hauser.technology and
// console.hauser.technology, a customer who signed up on one and was
// returned by Stripe to the other — success_url is built from APP_URL,
// a single fixed value — arrived with no session at all and was sent to
// login immediately after paying. Their subscription was fine; only
// the session was missing, which is why signing in again "fixed" it and
// made the whole thing look like a glitch.
//
// The edge now 301s the apex to the console, so this should never fire
// in production. It exists because edge rules can be bypassed by a
// stale bookmark, a proxy, a preview host, or simply a future hostname
// somebody forgets to cover — and the failure is silent: the app boots,
// stores state, and nobody can find it later.
//
// ---- Deliberately narrow ----
//
// Runs only when APP_URL names an http(s) origin different from the one
// we are on, and only outside development.
//
// PROJECT HOSTS ARE LEGITIMATE. `<slug>.hauser.md` is where a project
// lives (round 7), so bouncing it to the console would undo the feature
// — this used to be a plain origin comparison, and the comment claiming
// it "never touches project subdomains" was aspiration rather than
// code. Share links are served from the console origin and are
// unaffected either way.

export function enforceCanonicalOrigin(location = window.location) {
  if (!APP_URL) {
    return false;
  }

  let canonical;
  try {
    canonical = new URL(APP_URL);
  } catch {
    return false;
  }

  if (!/^https?:$/.test(canonical.protocol)) {
    return false;
  }

  // Local development runs on a different host by design.
  if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
    return false;
  }

  // A project's own address is canonical for that project.
  if (isProjectHost(location)) {
    return false;
  }

  if (location.origin === canonical.origin) {
    return false;
  }

  // `replace`, not `assign`: the wrong origin should not sit in history
  // where Back returns to it and starts the whole problem again.
  //
  // Path, query and hash are carried over — a deep link that arrived on
  // the wrong host is still a deep link, and dropping it would turn a
  // session bug into a navigation bug.
  location.replace(`${canonical.origin}${location.pathname}${location.search}${location.hash}`);
  return true;
}
